import {
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { Email, InvoiceHeader } from "app/model/entities/entity-model";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { EmailService } from "app/shared/email.service";
import { PrintFns } from "app/shared/print-fns";
import { ShippingService } from "app/shipping/shipping.service";
import { takeUntil } from "rxjs/operators";
import { InvoiceDialogComponent } from "app/shared/invoice-dialog.component";
import { UtilFns } from "app/shared/util-fns";

@Component({
  selector: "app-printable-invoice",
  templateUrl: "./printable-invoice.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PrintableInvoiceComponent
  extends DomainBaseComponent
  implements OnDestroy
{
  @ViewChild("printableArea") printElementRef: ElementRef;
  invoiceId: string;
  invoiceHeader: InvoiceHeader;
  /** whether to print all invoices for a shipment */
  isAllInvoices: boolean;
  invoiceHeaders: InvoiceHeader[];

  email: Email;
  private isAllLoaded: boolean;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,

    private emailService: EmailService,
    private shippingService: ShippingService,
    private elementRef: ElementRef
  ) {
    super(domainService);

    this.route.paramMap
      .pipe(takeUntil(this.onDestroy))
      .subscribe((paramMap) => {
        this.isAllInvoices = !!paramMap.get("all");
        this.updateFromContext();
      });
  }

  ngOnDestroy(): void {
    // PrintFns.setPrintFn(null);
  }

  async updateFromContext() {
    await this.dbQueryService.cacheAll();
    await this.shippingService.getShippingCarriers(); // just to cache everything
    this.invoiceId = this.route.snapshot.params["invoiceId"];

    this.invoiceHeader = await this.dbQueryService.getInvoice(this.invoiceId);
    if (this.invoiceHeader == null) {
      this.isPageReady = true;
      return;
    }
    this.invoiceHeaders = await this.dbQueryService.getInvoicesOnSameShipment(
      this.invoiceHeader.id
    );
    // sort so invoice with freight appears first
    this.invoiceHeaders.sort((a, b) => {
      return a.hasFreight ? -1 : b.hasFreight ? 1 : a.id.localeCompare(b.id);
    });

    // When all=true is passed, print all invoices on the same shipment

    if (this.isAllInvoices) {
      await this.loadAllInvoices();
    }

    this.isPageReady = true;

    setTimeout(() => {
      // if printing or headless browser, use print view
      const mediaQueryList = window.matchMedia("print");
      if (
        mediaQueryList.matches ||
        window.navigator.userAgent.includes("Headless")
      ) {
        this.preparePrint();
      } else {
        this.getInvoiceEmailStatus();
      }
    }, 0);
  }

  async onAllInvoicesChange(event) {
    if (this.isAllInvoices && !this.isAllLoaded) {
      await this.loadAllInvoices();
    }
  }

  /** Load invoices individually with JO and related data */
  private async loadAllInvoices() {
    this.isPageReady = false;
    const proms = this.invoiceHeaders.map((ih) =>
      this.dbQueryService.getInvoice(ih.id)
    );
    await Promise.all(proms);
    this.isAllLoaded = true;
    this.isPageReady = true;
  }


  preparePrint() {
    let el: HTMLElement =
      this.printElementRef && this.printElementRef.nativeElement;
    if (el == null) {
      el = this.elementRef && this.elementRef.nativeElement;
    }
    if (!el) {
      return;
    }
    PrintFns.printElement(el);
  }

  print() {
    this.preparePrint();
    setTimeout(() => window.print(), 0);
  }

  async emailInvoice() {
    const emailAddr =
      this.invoiceHeader?.joHeader?.billingEmail?.trim() ||
      this.invoiceHeader?.joHeader?.account?.invoiceEmail?.trim();

    if (!emailAddr) {
      await this.dialogService.showOkMessage(
        "No Email Address",
        "This invoice's Job Order has no billing email, and the Account has no invoice email on file."
      );
      return;
    }
    if (!UtilFns.validateMultipleEmail(emailAddr)) {
      await this.dialogService.showOkMessage(
        "Invalid Email Address",
        `The email address "${emailAddr}" is not valid.`
      );
      return;
    }

    const ok = await this.dialogService.yesNoDialog(
      "Resend Invoice",
      `Resend invoice ${this.invoiceId} to ${emailAddr}?`
    );
    if (!ok) return;

    await this.busyService.wrapBusy(async () => {
      const status = await this.emailService.sendInvoiceEmail(this.invoiceId, emailAddr);
      if (status === "accepted" || status === "stored") {
        this.toastr.success(`Invoice ${this.invoiceId} sent to ${emailAddr}`);
        await this.getInvoiceEmailStatus();
      } else {
        await this.dialogService.showOkMessage(
          "Email Failed",
          `Email server returned status: ${status ?? "unknown"}`
        );
      }
    });
  }

  async getInvoiceEmailStatus() {
    this.email = await this.emailService.getInvoiceEmailStatus(this.invoiceId);
  }

  async showInvoiceDialog() {
    await this.busyService.wrapBusy(async () => {
      await InvoiceDialogComponent.openDialog(this.pngDialogService, {
        invoiceId: this.invoiceId,
      });
    });
  }
}
